import logging from "utils/logging";
import _ from "lodash";
import React, { useState } from "react";
import { Button, Container, Typography } from "@mui/material";
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from "react-redux";
// import NumberFormat from "react-number-format";
import { NumericFormat } from "react-number-format";
import {
  selectCurrentQuestionObject,
  submitWagerAction,
} from "../question/questionSlice";
import AnswerTextField from "../question/components/AnswerTextField";
import { selectCurrentSubmission } from "../question/submissionSlice";
import { selectPlayer } from "../player/playerSlice";
import {
  selectGameId,
  selectGameStatusObject,
} from "../gamestate/gameStateSlice";
import { GAMESTATE_ROUND_FINAL } from "../gamestate/constants";

const log = logging.getLogger("Wagering"); //eslint-disable-line no-unused-vars

const Root = styled(Container)(({ theme }) => ({
  backgroundColor: '#fff',
  color: '#222',
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
}));

const CategoryHeader = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "1.5em",
  color: '#2ec4b6',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
}));

const MaxWagerButton = styled(Button)(({ theme }) => ({
  color: '#2ec4b6',
  borderColor: '#2ec4b6',
  marginBottom: theme.spacing(1),
  '&:hover': {
    backgroundColor: '#2ec4b6',
    color: '#fff',
  },
  '&:disabled': {
    color: '#ccc',
    borderColor: '#ccc',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#2ec4b6',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#25a8a0',
  },
  '&:disabled': {
    backgroundColor: '#ccc',
    color: '#666',
  },
}));

const DollarNumberFormat = (props) => {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.floatValue,
          },
        });
      }}
      inputMode="numeric"
      thousandSeparator
      isNumericString
      prefix="$"
    />
  );
};

const Wagering = () => {
  const dispatch = useDispatch();

  const [wager, setWager] = useState();
  const [submitting, setSubmitting] = useState(false);

  const gameId = useSelector(selectGameId);
  const gameState = useSelector(selectGameStatusObject);
  const questionObj = useSelector(selectCurrentQuestionObject);
  const submissionObject = useSelector(selectCurrentSubmission);
  const playerObject = useSelector(selectPlayer);

  const isDD = gameState.dailyDouble;
  const isFinal = gameState.round === GAMESTATE_ROUND_FINAL;

  if (!questionObj) {
    return <div />;
  }

  const onSubmit = () => {
    setSubmitting(true);
    log.info(
      "Submitting wager: ",
      gameId,
      questionObj.id,
      playerObject.id,
      wager
    );

    dispatch(submitWagerAction(gameId, questionObj.id, playerObject.id, wager));
  };

  const haveWager = submissionObject && submissionObject.wager;
  let minWager = isDD ? 5 : 0;
  let roundMax = gameState.round === "round1" ? 1000 : 2000;
  let maxWager = Math.max(isDD ? roundMax : 0, playerObject.score);

  const error = wager < minWager || wager > maxWager;
  let helperText = null;
  if (wager < minWager) {
    helperText = isDD
      ? "Your wager must be at least $5"
      : "Your wager must be zero or higher";
  } else if (wager > maxWager) {
    helperText = `Your wager must be ${
      "$" + maxWager.toLocaleString()
    } or less.`;
  }

  const onTDD = () => {
    setWager(maxWager);
  };

  const wagerArea = (
    <AnswerTextField
      error={error}
      helperText={helperText}
      id="final-wager-input"
      disabled={haveWager}
      label="Wager"
      value={haveWager ? submissionObject.wager : wager}
      onChange={(e) => setWager(e.target.value)}
      inputComponent={DollarNumberFormat}
    />
  );

  const maxButton = (
    <MaxWagerButton
      variant="outlined"
      onClick={onTDD}
      disabled={haveWager || submitting}
    >
      {isFinal ? "Risk it All" : "True Daily Double"}
    </MaxWagerButton>
  );

  let disabled =
    _.isNull(wager) ||
    _.isUndefined(wager) ||
    wager < minWager ||
    wager > maxWager;

  let buttonText = "Submit";
  if (haveWager) {
    buttonText = "Wager Locked";
    disabled = true;
  } else if (submitting) {
    buttonText = "Submitting...";
    disabled = true;
  }

  let submitButton = (
    <SubmitButton
      variant="contained"
      color="secondary"
      onClick={onSubmit}
      disabled={disabled}
    >
      {buttonText}
    </SubmitButton>
  );

  return (
    <Root maxWidth="sm">
      <CategoryHeader variant="h4" gutterBottom>
        {questionObj.category.toUpperCase()}
      </CategoryHeader>
      {wagerArea}
      {maxButton} <br />
      {submitButton}
    </Root>
  );
};

export { Wagering };
